import { useMemo, useState, useRef } from "react";
import type { TradeItem } from "./tradeTypes";
import { computeTotal, formatCurrency } from "./tradeHelpers";
import { useFocusTrap } from "./useFocusTrap";

type Props = {
  open: boolean;
  sent: TradeItem[];
  received: TradeItem[];
  onClose(): void;
  onCommit(): Promise<void>; // caller performs Firestore writes
};

export default function ConfirmTradeModal({ open, sent, received, onClose, onCommit }: Props) {
  const [verifying, setVerifying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const sentTotal = useMemo(() => computeTotal(sent), [sent]);
  const receivedTotal = useMemo(() => computeTotal(received), [received]);
  const diff = useMemo(() => receivedTotal - sentTotal, [receivedTotal, sentTotal]);
  const diffColor =
    diff === 0 ? "text-gray-300" : diff > 0 ? "text-emerald-400" : "text-amber-400";

  const panelRef = useRef<HTMLDivElement | null>(null);
  const commitBtnRef = useRef<HTMLButtonElement | null>(null);
  const yesBtnRef = useRef<HTMLButtonElement | null>(null);

  useFocusTrap({
    active: open,
    containerRef: panelRef,
    initialFocusRef: verifying ? yesBtnRef : commitBtnRef,
    onEscape: () => {
      if (!busy) onClose();
    },
  });

  if (!open) return null;

  async function handleCommit() {
    setBusy(true);
    setErr(null);
    try {
      await onCommit();
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Trade commit failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" aria-hidden={!open}>
      <div className="absolute inset-0 bg-black/70" onClick={() => !busy && onClose()} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-trade-title"
        className="relative z-10 w-full max-w-2xl rounded-md border border-gray-800 bg-gray-950 p-4 shadow-2xl"
        tabIndex={-1}
      >
        <div className="mb-3 flex items-center justify-between">
          <div id="confirm-trade-title" className="text-lg font-semibold text-gray-100">Confirm Trade</div>
          <button
            className="rounded border border-gray-700 bg-black/40 px-2 py-1 text-gray-200 hover:bg-white/5 disabled:opacity-60"
            onClick={onClose}
            disabled={busy}
            aria-label="Close confirm trade"
          >
            Close
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-4 text-sm">
            <div className="text-gray-300">
              Sending: <span className="font-medium text-gray-100">{formatCurrency(sentTotal)}</span> ({sent.length} items)
            </div>
            <div className="text-gray-300">
              Receiving: <span className="font-medium text-gray-100">{formatCurrency(receivedTotal)}</span> ({received.length} items)
            </div>
            <div className={`font-semibold ${diffColor}`}>
              Diff: {formatCurrency(diff)} {diff === 0 ? "" : diff > 0 ? "(in your favor)" : "(you owe)"}
            </div>
          </div>

          <div className="rounded border border-gray-800 bg-black/20 p-2 text-xs text-gray-400">
            Note: Committing will decrement items you are sending from your collection (if selected from your collection)
            and add received items as new entries marked “SGtraded.” This does not modify other users’ collections.
          </div>

          {err && (
            <div className="rounded border border-red-800 bg-red-900/20 p-2 text-sm text-red-200">{err}</div>
          )}
        </div>

        {!verifying ? (
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              className="rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-200 hover:bg-white/5"
              onClick={onClose}
              disabled={busy}
            >
              Calculate Only
            </button>
            <button
              ref={commitBtnRef}
              className="bg-amber-500 px-3 py-2 font-medium text-black hover:bg-amber-400 disabled:opacity-60"
              onClick={() => setVerifying(true)}
              disabled={busy}
            >
              Commit to My Collection
            </button>
          </div>
        ) : (
          <div className="mt-4 rounded border border-amber-700 bg-amber-900/20 p-3">
            <div className="mb-3 text-sm text-amber-100">
              Are you sure you want to commit this trade to your collection? This action will update your collection.
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                className="rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-200 hover:bg-white/5"
                onClick={() => setVerifying(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                ref={yesBtnRef}
                className="rounded border border-amber-800 bg-amber-500/20 px-3 py-2 font-medium text-amber-200 hover:bg-amber-500/30 disabled:opacity-60"
                onClick={() => void handleCommit()}
                disabled={busy}
              >
                {busy ? "Committing…" : "Yes, Commit"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
